import React, { isValidElement, ReactElement, ComponentType } from "react";
import cx from "classnames";
import "./IconToggle.scss";
import { Icon, type IconName } from "../Icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../Tooltip/Tooltip";
import type { Placement } from "@floating-ui/react";

type IconToggleProps = {
  iconName?: IconName | ReactElement | ComponentType<any>;
  toggled?: boolean;
  onChange?: (toggled: boolean) => void;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  iconProps?: Record<string, any>;
  size?: "sm" | "xs";
  tooltip?: string | null;
  tooltipPlacement?: Placement;
};

const IconToggle: React.FC<IconToggleProps> = ({
  iconName,
  toggled = false,
  onChange,
  disabled = false,
  className,
  ariaLabel,
  iconProps = {},
  size = "sm",
  tooltip = null,
  tooltipPlacement = "top",
}) => {
  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!toggled);
    }
  };

  const toggleClasses = cx(
    "IconToggle",
    {
      "IconToggle--Toggled": toggled,

      "IconToggle--Small": size === "sm",
      "IconToggle--XSmall": size === "xs",
    },
    className,
  );

  const renderIcon = () => {
    if (!iconName) return null;

    if (typeof iconName === "string") {
      return <Icon iconName={iconName as IconName} {...iconProps} />;
    }

    if (isValidElement(iconName)) {
      return iconName;
    }

    const IconComponent = iconName as ComponentType<any>;
    return <IconComponent {...iconProps} />;
  };

  return (
    <Tooltip placement={tooltipPlacement}>
      <TooltipTrigger>
        <button
          type="button"
          className={toggleClasses}
          onClick={handleClick}
          disabled={disabled}
          aria-pressed={toggled}
          aria-label={ariaLabel}
        >
          <span className="IconToggle__IconWrapper">{renderIcon()}</span>
        </button>
      </TooltipTrigger>
      {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
    </Tooltip>
  );
};

export { IconToggle };
